import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import {
  RobotOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  BulbOutlined,
  LogoutOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { HOME_KEY, destinationByKey, moduleGlyph } from '@/layouts/navigation';
import { trackEvent } from '@/lib/telemetry';
import { switchPractice } from './practiceSwitch';
import { useAgentDirectory } from './useAgentDirectory';
import { agentStatusLabel, agentStatusOf } from './agentStatus';

/** Agents listed at a time: the server answers the text as typed, so the first few are the ones that match. */
const PALETTE_AGENTS = 30;

// Linear-style command palette. Cmd+K to open. Searches:
//   - Recent items (top) — page links only while their page is discoverable
//   - Go to: Home + every destination the resolved navigation offers this person, labelled with its module
//     (choosing one switches module, because the module follows the URL). Hidden modules' pages and pages
//     the person may not open are never listed.
//   - Modules: open a module on its landing page
//   - Commands (theme toggle, sign out)
//   - Merchants (live data from MerchantContextStore)
//   - Agents (the SERVER's directory for the text as typed — current practice only; only while Agents is discoverable).
//     It used to offer the first 30 of the 100 agents a store held, so most of a practice's agents could not be reached.

export const CommandPalette = observer(function CommandPalette() {
  const { commandPaletteStore: cp, merchantContextStore: ctx, agentRegistryStore: reg, themeStore, authStore, studioModuleStore: sm } =
    useStore();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const agentsShown = cp.isOpen && sm.isLinkDiscoverable('/agents') && ctx.selectedMerchantId != null;
  const { rows: agents } = useAgentDirectory(
    agentsShown ? { merchantId: ctx.selectedMerchantId!, search: text.trim() || undefined, pageSize: PALETTE_AGENTS } : null,
  );

  // Focus the input whenever the palette opens.
  useEffect(() => {
    if (cp.isOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
    setText('');
    return undefined;
  }, [cp.isOpen]);

  const close = (): void => cp.close();

  const go = (href: string, label: string, id?: string): void => {
    if (id) cp.recordRecent({ id, label, href });
    trackEvent('CommandPalette.NavSelected', { href });
    close();
    navigate(href);
  };

  const styles = useMemo(() => buildStyles(themeStore.mode === 'dark'), [themeStore.mode]);
  const home = destinationByKey(HOME_KEY);

  if (!cp.isOpen) return null;

  // Every shortcut obeys the same discoverability as Go to: a Recent link or an agent whose page sits in a hidden module
  // (or in none) is not advertised. A Recent entry with no link (a merchant switch) is not a page and stays.
  const recent = cp.recent.filter((r) => !r.href || sm.isLinkDiscoverable(r.href));

  return (
    <Command.Dialog
      open={cp.isOpen}
      onOpenChange={(o) => (o ? cp.open() : cp.close())}
      label="Command palette"
      className="agent-studio-cmdk"
      style={styles.overlay}
      onKeyDown={(e) => {
        if (e.key === 'Escape') close();
      }}
    >
      <div style={styles.dialog}>
        <div style={styles.inputWrap}>
          <Command.Input
            ref={inputRef}
            value={text}
            onValueChange={setText}
            placeholder="Search agents, merchants, commands… "
            style={styles.input}
          />
          <kbd style={styles.kbd}>esc</kbd>
        </div>

        <Command.List style={styles.list}>
          <Command.Empty style={styles.empty}>No matches.</Command.Empty>

          {recent.length > 0 && (
            <Command.Group heading="Recent" style={styles.group}>
              {recent.map((r) => (
                <Command.Item
                  key={r.id}
                  value={`recent ${r.label}`}
                  onSelect={() => r.href && go(r.href, r.label, r.id)}
                  style={styles.item}
                >
                  <ClockCircleOutlined style={styles.itemIcon} />
                  <span>{r.label}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {/* Home + every destination the resolved navigation offers — the same model the rail and launcher read. */}
          <Command.Group heading="Go to" style={styles.group}>
            {home && (
              <Command.Item key="nav-home" value="go home dashboard" onSelect={() => go(home.to, home.label)} style={styles.item}>
                <span style={styles.itemIcon}>{home.icon}</span>
                <span>{home.label}</span>
                <span style={styles.itemHint}>Everywhere</span>
              </Command.Item>
            )}
            {sm.discoverable.map(({ destination, module }) => (
              <Command.Item
                key={`nav-${destination.key}`}
                value={`go ${destination.label} ${module.title} ${destination.key} ${destination.scope}`}
                onSelect={() => go(destination.to, destination.label)}
                style={styles.item}
              >
                <span style={styles.itemIcon}>{destination.icon}</span>
                <span>{destination.label}</span>
                <span style={styles.itemHint}>
                  {module.title}
                  {destination.scope === 'platform' ? ' · platform' : destination.scope === 'both' ? ' · platform + merchant' : ''}
                </span>
              </Command.Item>
            ))}
          </Command.Group>

          {sm.modules.length > 0 && (
            <Command.Group heading="Modules" style={styles.group}>
              {sm.modules.map((m) => (
                <Command.Item
                  key={`module-${m.key}`}
                  value={`module open ${m.title} ${m.key}`}
                  onSelect={() => go(m.landing.to, m.title)}
                  style={styles.item}
                >
                  <span style={styles.itemIcon}>{moduleGlyph(m.iconKey)}</span>
                  <span>{m.title}</span>
                  <span style={styles.itemHint}>opens {m.landing.label}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          <Command.Group heading="Commands" style={styles.group}>
            <Command.Item
              value="toggle theme dark light"
              onSelect={() => {
                themeStore.toggleMode();
                close();
              }}
              style={styles.item}
            >
              <BulbOutlined style={styles.itemIcon} />
              <span>Toggle {themeStore.mode === 'dark' ? 'light' : 'dark'} mode</span>
              <span style={styles.itemHint}>{themeStore.mode === 'dark' ? '☼' : '☾'}</span>
            </Command.Item>
            <Command.Item
              value="sign out logout"
              onSelect={() => {
                close();
                authStore.logout();
              }}
              style={styles.item}
            >
              <LogoutOutlined style={styles.itemIcon} />
              <span>Sign out</span>
            </Command.Item>
          </Command.Group>

          {ctx.merchants.length > 0 && (
            <Command.Group heading="Switch merchant" style={styles.group}>
              {ctx.merchants.slice(0, 12).map((m) => (
                <Command.Item
                  key={`merchant-${m.id}`}
                  value={`merchant ${m.title} ${m.codeName ?? ''}`}
                  onSelect={() => {
                    close();
                    void switchPractice(ctx, m.id, () => {
                      cp.recordRecent({ id: `merchant:${m.id}`, label: m.title, icon: 'shop' });
                      trackEvent('CommandPalette.MerchantSwitched', { merchantId: m.id });
                    });
                  }}
                  style={styles.item}
                >
                  <ShopOutlined style={styles.itemIcon} />
                  <span>{m.title}</span>
                  {m.id === ctx.selectedMerchantId && <span style={styles.itemHint}>current</span>}
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {agents.length > 0 && (
            <Command.Group heading={`Agents in ${ctx.selectedMerchant?.title ?? 'merchant'}`} style={styles.group}>
              {/* The server's matches for the text (names, canonical name, description, id); the keywords let cmdk's own
                  filter keep a row the server matched on something other than its name. */}
              {agents.map((a) => {
                const pinned = reg.pinnedAgentIds.has(a.id);
                return (
                  <Command.Item
                    key={`agent-${a.id}`}
                    value={`agent ${a.displayName ?? ''} ${a.a2AAgentName ?? ''} #${a.id}`}
                    keywords={[a.canonicalName, a.a2AAgentName, a.description, String(a.id)].filter((k): k is string => !!k)}
                    // Navigate STRAIGHT to the agent's detail page — power-
                    // user shortcut. The store's trackOpened() pushes this
                    // onto the per-merchant Recent list automatically when
                    // AgentDetailPage mounts.
                    onSelect={() =>
                      go(`/agents/${a.id}`, a.displayName ?? `Agent #${a.id}`, `agent:${a.id}`)
                    }
                    style={styles.item}
                  >
                    <RobotOutlined style={styles.itemIcon} />
                    <span>{a.displayName ?? `Agent #${a.id}`}</span>
                    <span style={styles.itemHint}>{agentStatusLabel(agentStatusOf(a))}</span>
                    {pinned && <span style={{ ...styles.itemHint, color: colors.amber[500] }}>★</span>}
                    {a.a2AAgentName && (
                      <span style={{ ...styles.itemHint, fontFamily: "'JetBrains Mono', monospace" }}>
                        {a.a2AAgentName}
                      </span>
                    )}
                  </Command.Item>
                );
              })}
            </Command.Group>
          )}

          <div style={styles.footer}>
            <ThunderboltOutlined style={{ marginRight: 6 }} />
            Cmd+K to open · ↑↓ to navigate · ↵ to select · esc to close
          </div>
        </Command.List>
      </div>
    </Command.Dialog>
  );
});

// Inline styles instead of CSS modules so the palette is fully self-contained
// and easy to lift into a shared package later.
function buildStyles(dark: boolean) {
  const bg = dark ? colors.slate[800] : '#FFFFFF';
  const fg = dark ? colors.slate[50] : colors.slate[900];
  const muted = dark ? colors.slate[400] : colors.slate[500];
  const border = dark ? colors.slate[700] : colors.slate[200];
  const groupBg = 'transparent';
  const itemHover = dark ? colors.slate[700] : colors.ink[50];
  return {
    overlay: {
      position: 'fixed' as const,
      inset: 0,
      background: 'rgba(15, 23, 42, 0.5)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: '15vh',
      zIndex: 2000,
    },
    dialog: {
      width: 'min(640px, 92vw)',
      background: bg,
      color: fg,
      border: `1px solid ${border}`,
      borderRadius: 14,
      boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
      overflow: 'hidden',
    },
    inputWrap: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '14px 16px',
      borderBottom: `1px solid ${border}`,
    },
    input: {
      flex: 1,
      border: 'none',
      outline: 'none',
      background: 'transparent',
      color: fg,
      fontSize: 15,
      fontFamily: 'inherit',
    },
    kbd: {
      fontSize: 11,
      padding: '2px 6px',
      borderRadius: 4,
      background: dark ? colors.slate[700] : colors.slate[100],
      color: muted,
      fontFamily: "'JetBrains Mono', monospace",
    },
    list: {
      maxHeight: 420,
      overflow: 'auto',
      padding: 6,
    },
    group: {
      background: groupBg,
      padding: 4,
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 10px',
      borderRadius: 8,
      cursor: 'pointer',
      fontSize: 14,
      color: fg,
    },
    itemIcon: {
      color: muted,
      fontSize: 14,
    },
    itemHint: {
      marginLeft: 'auto',
      fontSize: 12,
      color: muted,
    },
    empty: {
      padding: '20px 12px',
      textAlign: 'center' as const,
      color: muted,
      fontSize: 13,
    },
    footer: {
      borderTop: `1px solid ${border}`,
      padding: '8px 12px',
      fontSize: 11,
      color: muted,
      textAlign: 'center' as const,
      marginTop: 4,
      itemHover, // keep referenced so the linter doesn't flag
    } as React.CSSProperties,
  };
}
